// src/components/product/ProductDetail.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Star, 
  ShoppingCart, 
  Heart, 
  Share2, 
  ChevronRight, 
  ChevronLeft,
  Minus, 
  Plus, 
  MessageSquare, 
  User, 
  Send, 
  AlertCircle
} from 'lucide-react';
import { productService } from '../services/productService';
import { reviewService } from '../services/reviewService';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart, updateQuantity, toggleWishlist, wishlist, cart } = useCart();
  const { isAuthenticated } = useAuth();

  // Primary State
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState(
    location.state?.activeTab === 'reviews' ? 'reviews' : 'description'
  );

  // Review State
  const [productReviews, setProductReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  // Fetch Product
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await productService.getProduct(id, { signal: controller.signal });
        if (isMounted) {
          setProduct(response.data);
          setSelectedImage(0);
        }
      } catch (error) {
        if (error.name !== 'CanceledError' && isMounted) {
          toast.error(error.response?.data?.message || 'Failed to load product details');
          navigate('/store', { replace: true });
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (id) fetchProduct();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [id, navigate]);

  // Fetch Reviews
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchProductReviews = async () => {
      if (activeTab !== 'reviews' || !id) return;
      setReviewsLoading(true);
      try {
        const response = await reviewService.getProductReviews(id, { signal: controller.signal });
        if (isMounted) setProductReviews(response.data || []);
      } catch (error) {
        if (error.name !== 'CanceledError' && isMounted) {
          console.error('Failed to load product reviews:', error);
          setProductReviews([]);
        }
      } finally {
        if (isMounted) setReviewsLoading(false);
      }
    };

    fetchProductReviews();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [id, activeTab]);

  // Calculations
  const existingCartQuantity = useMemo(() => {
    const item = cart?.find((i) => (i._id || i.id) === id);
    return item ? item.quantity : 0;
  }, [cart, id]);

  const maxSelectableQuantity = useMemo(() => {
    if (!product) return 0;
    return Math.max(0, product.stock - existingCartQuantity);
  }, [product, existingCartQuantity]);

  const isInWishlist = useMemo(() => {
    return wishlist?.some((item) => item._id === product?._id) || false;
  }, [wishlist, product]);

  const allImages = useMemo(() => {
    if (!product) return [];
    if (product.images && product.images.length > 0) {
      return product.images.map((img) => (typeof img === 'string' ? img : img.url));
    }
    return product.image ? [product.image] : ['/placeholder-image.png'];
  }, [product]);

  // Gallery
  const nextImage = useCallback((e) => {
    e?.stopPropagation();
    setSelectedImage((prev) => (prev + 1) % allImages.length);
  }, [allImages.length]);

  const prevImage = useCallback((e) => {
    e?.stopPropagation();
    setSelectedImage((prev) => (prev - 1 + allImages.length) % allImages.length);
  }, [allImages.length]);

  const handleShare = async (e) => {
    e.stopPropagation();
    const shareData = {
      title: product.name,
      text: `Check out ${product.name} - ₹${product.price}`,
      url: window.location.href,
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        return;
      }
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Product link copied to clipboard!');
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Could not share. Please copy link manually.');
      }
    }
  };

  // --- ADD TO CART (Sends exact quantity) ---
  const handleAddToCart = () => {
    if (quantity > maxSelectableQuantity) {
      toast.error(`Only ${maxSelectableQuantity} more items available.`);
      return;
    }

    const productId = product._id || product.id;
    const existingItem = cart?.find((i) => (i._id || i.id) === productId);

    if (existingItem && updateQuantity) {
      updateQuantity(productId, Math.min(product.stock, existingItem.quantity + quantity));
    } else {
      addToCart({ ...product, quantity });
    }

    toast.success(`Added ${quantity} item(s) to cart!`);
    setQuantity(1);
  };

  // --- BUY NOW (Sends exact quantity & redirects) ---
  const handleBuyNow = () => {
    if (quantity > maxSelectableQuantity) {
      toast.error(`Only ${maxSelectableQuantity} more items available.`);
      return;
    }

    const productId = product._id || product.id;
    const existingItem = cart?.find((i) => (i._id || i.id) === productId);

    if (existingItem && updateQuantity) {
      updateQuantity(productId, Math.min(product.stock, existingItem.quantity + quantity));
    } else {
      addToCart({ ...product, quantity });
    }

    navigate('/cart', {
      state: {
        targetProductId: productId
      }
    });
  };

  const handleToggleWishlist = () => {
    toggleWishlist(product);
    toast.success(isInWishlist ? 'Removed from wishlist' : 'Added to wishlist');
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please login to write a review');
      navigate('/login', { state: { from: location } });
      return;
    }
    if (!reviewForm.comment.trim()) {
      toast.error('Please write a comment');
      return;
    }

    setSubmittingReview(true);
    try {
      const response = await reviewService.createReview({
        product: id,
        rating: reviewForm.rating,
        title: reviewForm.title,
        comment: reviewForm.comment,
      });

      toast.success('Review submitted successfully!');
      setReviewForm({ rating: 5, title: '', comment: '' });
      
      if (response.data) {
        setProductReviews((prev) => [response.data, ...prev]);
        setProduct((prev) => ({
          ...prev,
          numReviews: (prev.numReviews || 0) + 1,
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-1 max-w-5xl mx-auto px-6 py-10 w-full animate-pulse space-y-8">
          <div className="h-4 bg-slate-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5 aspect-square bg-slate-100 rounded-2xl"></div>
            <div className="lg:col-span-7 space-y-4">
              <div className="h-8 bg-slate-200 rounded w-3/4"></div>
              <div className="h-6 bg-slate-200 rounded w-1/3"></div>
              <div className="h-10 bg-slate-100 rounded-lg w-full"></div>
            </div>
          </div>
        </main>
        <footer role="contentinfo" className="mt-16 border-t border-slate-100 bg-white w-full">
          <Footer />
        </footer>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased selection:bg-slate-100 flex flex-col">
      <Header />

      <main className="flex-1 max-w-5xl mx-auto px-6 py-4 w-full">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-400 mb-6 tracking-wide">
          <span 
            className="cursor-pointer hover:text-slate-900 transition-colors" 
            onClick={() => navigate('/store')}
          >
            Store
          </span>
          <ChevronRight size={12} className="text-slate-300" />
          <span 
            className="cursor-pointer hover:text-slate-900 transition-colors"
            onClick={() => navigate(`/store?category=${encodeURIComponent(product.category)}`)}
          >
            {product.category}
          </span>
          <ChevronRight size={12} className="text-slate-300" />
          <span className="text-slate-600 truncate max-w-[180px] font-medium">{product.name}</span>
        </nav>

        <article className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5 space-y-4">
            <div className="group relative border border-slate-200/80 rounded-2xl overflow-hidden bg-gradient-to-b from-slate-50/50 to-white flex items-center justify-center aspect-square max-h-[400px]">
              <img
                src={allImages[selectedImage]}
                alt={`${product.name} view ${selectedImage + 1}`}
                className="max-w-full max-h-full object-contain p-6 mix-blend-multiply transition-transform duration-300 group-hover:scale-105"
                loading="eager"
              />

              {allImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-3 p-2 rounded-full bg-white/90 backdrop-blur-sm text-slate-700 border border-slate-200 shadow-sm opacity-0 group-hover:opacity-100 transition-all hover:bg-white active:scale-95"
                    aria-label="Previous Image"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-3 p-2 rounded-full bg-white/90 backdrop-blur-sm text-slate-700 border border-slate-200 shadow-sm opacity-0 group-hover:opacity-100 transition-all hover:bg-white active:scale-95"
                    aria-label="Next Image"
                  >
                    <ChevronRight size={16} />
                  </button>
                </>
              )}

              <button
                onClick={handleShare}
                className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm border border-slate-200 rounded-full hover:bg-white shadow-sm transition-all z-10 active:scale-95"
                aria-label="Share product"
              >
                <Share2 size={14} className="text-slate-600 hover:text-slate-900" />
              </button>
            </div>

            {allImages.length > 1 && (
              <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-thin snap-x">
                {allImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    aria-label={`Select image ${index + 1}`}
                    className={`flex-shrink-0 w-16 h-16 rounded-xl border bg-slate-50 flex items-center justify-center p-1.5 transition-all snap-start ${
                      selectedImage === index 
                        ? 'border-slate-950 ring-2 ring-slate-950/5 opacity-100 shadow-sm' 
                        : 'border-slate-200 opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt="" className="max-h-full max-w-full object-contain mix-blend-multiply" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-7 space-y-5">
            <div className="space-y-2">
              {product.condition && (
                <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold tracking-wider uppercase ${
                  product.condition === 'New' 
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                    : 'bg-amber-50 text-amber-700 border border-amber-100'
                }`}>
                  {product.condition}
                </span>
              )}
              
              <h1 className="text-xl font-bold tracking-tight text-slate-950 leading-snug">
                {product.name}
              </h1>
              
              <div className="flex items-center gap-2 text-xs">
                <div className="flex items-center gap-0.5">
                  <Star className="text-amber-400 fill-amber-400" size={12} />
                  <span className="font-semibold text-slate-700">{product.rating || 0}</span>
                </div>
                <span className="text-slate-400">({product.numReviews || 0} reviews)</span>
              </div>
            </div>

            <div className="pt-3 border-t border-slate-100">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-extrabold tracking-tight text-slate-950">₹{product.price}</span>
                {product.mrp && product.mrp > product.price && (
                  <>
                    <span className="text-sm text-slate-400 line-through">₹{product.mrp}</span>
                    <span className="text-xs text-emerald-600 font-semibold tracking-wide">
                      {Math.round((1 - product.price / product.mrp) * 100)}% OFF
                    </span>
                  </>
                )}
              </div>

              <div className="text-[11px] mt-1">
                {maxSelectableQuantity > 0 ? (
                  <span className="text-emerald-600 flex items-center gap-1 font-medium">
                    ✓ {product.stock} items remaining
                  </span>
                ) : product.stock > 0 ? (
                  <span className="text-amber-600 flex items-center gap-1 font-medium">
                    <AlertCircle size={12} /> Reached cart limit
                  </span>
                ) : (
                  <span className="text-rose-600 font-medium">Out of Stock</span>
                )}
              </div>
            </div>

            {/* SINGLE-LINE ACTION BAR */}
            <div className="pt-2 flex items-center gap-2">
              <div className="flex items-center border border-slate-200 rounded-lg bg-white overflow-hidden shadow-sm shrink-0">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-2 py-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors disabled:opacity-40"
                  disabled={maxSelectableQuantity === 0 || quantity <= 1}
                  aria-label="Decrease quantity"
                >
                  <Minus size={12} />
                </button>
                <span className="w-6 text-center text-xs font-semibold text-slate-800 select-none">{quantity}</span>
                <button
                  onClick={() => setQuantity(Math.min(maxSelectableQuantity, quantity + 1))}
                  className="px-2 py-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors disabled:opacity-40"
                  disabled={maxSelectableQuantity === 0 || quantity >= maxSelectableQuantity}
                  aria-label="Increase quantity"
                >
                  <Plus size={12} />
                </button>
              </div>

              <button
                onClick={handleBuyNow}
                disabled={maxSelectableQuantity === 0}
                className="flex-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all font-medium text-xs tracking-wide flex items-center justify-center gap-1 shadow-sm disabled:bg-slate-200 disabled:text-slate-400 active:scale-95 whitespace-nowrap"
              >
                <span>Buy Now</span>
                <ChevronRight size={13} />
              </button>

              <button
                onClick={handleAddToCart}
                disabled={maxSelectableQuantity === 0}
                className="flex-1 px-3 py-1.5 bg-slate-950 hover:bg-slate-800 text-white rounded-lg transition-all font-medium text-xs tracking-wide flex items-center justify-center gap-1 shadow-sm disabled:bg-slate-200 disabled:text-slate-400 active:scale-95 whitespace-nowrap"
              >
                <ShoppingCart size={13} />
                <span>Add to Cart</span>
              </button>

              <button
                onClick={handleToggleWishlist}
                title={isInWishlist ? "Remove from Wishlist" : "Add to Wishlist"}
                aria-label={isInWishlist ? "Remove from Wishlist" : "Add to Wishlist"}
                className={`p-2 rounded-lg border text-xs transition-colors shadow-sm shrink-0 active:scale-95 ${
                  isInWishlist 
                    ? 'border-rose-200 bg-rose-50 text-rose-600' 
                    : 'border-slate-200 text-slate-600 hover:border-slate-400 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                <Heart size={13} className={isInWishlist ? 'fill-current' : ''} />
              </button>
            </div>

            {product.vendor && (
              <div className="pt-3 border-t border-slate-100 text-xs flex gap-1.5">
                <span className="text-slate-400">Sold by:</span>
                <span className="font-medium text-slate-800 hover:underline cursor-pointer">{product.vendor.name}</span>
              </div>
            )}
          </div>
        </article>

        {/* Tab System */}
        <section aria-label="Product Information and Reviews" className="mt-12 border-t border-slate-100 pt-8">
          <div className="flex gap-6 mb-4 text-xs" role="tablist">
            {['description', 'specifications', 'reviews'].map((tab) => (
              <button
                key={tab}
                role="tab"
                aria-selected={activeTab === tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-2 font-semibold capitalize tracking-wide transition-all border-b-2 ${
                  activeTab === tab
                    ? 'text-slate-950 border-slate-950'
                    : 'text-slate-400 border-transparent hover:text-slate-600'
                }`}
              >
                {tab === 'reviews' ? `Reviews (${product.numReviews || 0})` : tab}
              </button>
            ))}
          </div>

          <div className="text-slate-600 text-xs leading-relaxed max-w-2xl">
            {activeTab === 'description' && (
              <div className="whitespace-pre-line text-slate-700">
                {product.description}
              </div>
            )}

            {activeTab === 'specifications' && product.specifications && (
              <div className="border border-slate-100 rounded-lg overflow-hidden bg-white max-w-md shadow-sm">
                {Object.entries(product.specifications).map(([key, value], i) => (
                  <div 
                    key={key} 
                    className={`flex justify-between p-3 text-xs ${
                      i % 2 === 0 ? 'bg-slate-50/40' : 'bg-white'
                    } ${i !== 0 ? 'border-t border-slate-100' : ''}`}
                  >
                    <span className="text-slate-500 capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="font-medium text-slate-800">{String(value)}</span>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="space-y-4">
                <div className="bg-white border border-slate-100 rounded-xl p-4 sm:p-5 shadow-sm">
                  <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                    <MessageSquare size={14} />
                    Write a Review
                  </h3>
                  <form onSubmit={handleSubmitReview} className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">Your Rating</label>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setReviewForm((prev) => ({ ...prev, rating: star }))}
                            className="p-0.5 transition-transform hover:scale-110"
                          >
                            <Star
                              size={18}
                              className={star <= reviewForm.rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}
                            />
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">Title (optional)</label>
                      <input
                        type="text"
                        value={reviewForm.title}
                        onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                        placeholder="Summarize your review..."
                        className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-1 focus:ring-slate-950 outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">Review</label>
                      <textarea
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                        placeholder="Share your experience..."
                        rows={3}
                        className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-1 focus:ring-slate-950 outline-none"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submittingReview || !isAuthenticated}
                      className="flex items-center gap-1.5 bg-slate-950 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-slate-800 transition disabled:bg-slate-200 disabled:text-slate-400"
                    >
                      <Send size={12} />
                      {submittingReview ? 'Submitting...' : isAuthenticated ? 'Submit Review' : 'Login to Review'}
                    </button>
                  </form>
                </div>

                {reviewsLoading ? (
                  <div className="text-center py-8 text-xs text-slate-400 animate-pulse">Loading reviews...</div>
                ) : productReviews.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50/50 rounded-xl border border-slate-100">
                    <p className="text-xs text-slate-500">No reviews yet. Be the first to write one!</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {productReviews.map((review) => (
                      <div key={review._id} className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-600 text-[10px]">
                              {review.user?.name ? review.user.name.charAt(0).toUpperCase() : <User size={12} />}
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-slate-800">{review.user?.name || 'Anonymous'}</p>
                              <div className="flex items-center gap-0.5">
                                {Array.from({ length: 5 }, (_, i) => (
                                  <Star
                                    key={i}
                                    size={10}
                                    className={i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200'}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                          <span className="text-[10px] text-slate-400">
                            {new Date(review.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        {review.title && <h4 className="text-xs font-bold text-slate-800 mb-1">{review.title}</h4>}
                        <p className="text-xs text-slate-600">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </main>

      <footer role="contentinfo" className="mt-16 border-t border-slate-100 bg-white w-full shrink-0">
        <Footer />
      </footer>
    </div>
  );
};

export default ProductDetail;